/**
 * The pay page's item basket — the arithmetic and the validation, with no React in it.
 *
 * This app has jest but no @testing-library/react, and adding one to a 2020 lockfile is not
 * worth it for this. So everything that can be *wrong* lives here and is tested; the
 * components around it are verified in the browser.
 *
 * What "wrong" means here is a number. The server charges from its own stored snapshot on
 * every path, so a mistake in this file cannot overcharge anybody — it does something
 * arguably worse, which is show a payer one figure and take another.
 */

/** Is this row an item basket, or the document request every row was before item pay? */
export const isItems = (data) =>
  !!data && !!data.jsond_doc && data.jsond_doc.kind === 'items'

const money = (n) => Math.round((Number(n) || 0) * 100) / 100

/**
 * One line's total, rounded.
 *
 * Rounded HERE, per line, and only then summed — the same order PayLink::snapshot() uses on
 * the server. Summing first and rounding once gives a different answer often enough to
 * matter, and "often enough" on a payment page means a support call about a one-agora gap
 * between the page and the receipt.
 */
export const lineTotal = (line) =>
  money((Number(line && line.price) || 0) * (Number(line && line.qty) || 0))

export const cartTotal = (lines) =>
  money((Array.isArray(lines) ? lines : []).reduce((sum, line) => sum + lineTotal(line), 0))

// Same table Checkout.js carried inline; it lives here now so there is one copy.
const SYMBOLS = {
  USD: '$', EUR: '€', CRC: '₡', GBP: '£', ILS: '₪', INR: '₹', JPY: '¥', KRW: '₩',
  NGN: '₦', PHP: '₱', PLN: 'zł', PYG: '₲', THB: '฿', UAH: '₴', VND: '₫',
}

/**
 * An amount as a payer should read it.
 *
 * Always two decimals: "320" reads like a rounded estimate and "320.00" reads like a price,
 * and this is the number somebody is about to approve. An unrecognised currency shows its
 * code rather than nothing, because a bare number with no unit is worse than an odd one.
 */
export const formatMoney = (amount, currency) => {
  const value = money(amount).toFixed(2)
  const symbol = SYMBOLS[currency] || currency || ''

  return symbol ? value + ' ' + symbol : value
}

// Deliberately loose. The server validates properly (PaymentRequest::validateCustomer, which
// also checks length and refuses header-injecting addresses); this only has to catch the
// typo before the payer waits on a round trip for it.
const EMAIL = /^[^\s@,;]+@[^\s@,;.]+\.[^\s@,;]+$/

/**
 * The payer's details.
 *
 * Returns `{}` when they are usable, or `{field: 'translationKey'}` per bad field. EVERY bad
 * field at once, not the first: fixing one thing, resubmitting and being told about the next
 * is the worst version of this form.
 */
export const validateCustomer = (values, collect) => {
  const v = values || {}
  const errors = {}

  if (!String(v.name || '').trim()) {
    errors.name = 'RequiredField'
  }

  const email = String(v.email || '').trim()
  if (!email) {
    errors.email = 'RequiredField'
  } else if (!EMAIL.test(email)) {
    errors.email = 'InvalidEmail'
  }

  // Only when the link asked for it. The box is not rendered otherwise, and validating a
  // field a payer cannot see would block them on something they have no way to fix.
  if (collect && collect.vatnum) {
    const vatnum = String(v.vatnum || '').trim()
    if (!vatnum) {
      errors.vatnum = 'RequiredField'
    } else if (!/^\d+$/.test(vatnum)) {
      errors.vatnum = 'InvalidVatNum'
    }
  }

  // No phone check on purpose: the server normalises an unusable number to '' rather than
  // refusing it, and losing a sale over a badly typed phone number is a bad trade.

  return errors
}
